/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { create } from 'zustand';
import { LEAD_AD_CONTENT_DEFAULTS, type FacebookSettings } from './facebook-settings.helpers';

/** A development-only scenario key for the Facebook settings mock. */
export type FacebookSettingsMockScenario =
  | 'api'
  | 'disconnected'
  | 'connected'
  | 'connected-no-payment';

interface FacebookSettingsMockState {
  scenario: FacebookSettingsMockScenario;
  setScenario: (s: FacebookSettingsMockScenario) => void;
}

const STORAGE_KEY = 'dev:fb-settings-scenario';

/** Zustand store backing the dev-only Facebook settings scenario picker. */
export const useFacebookSettingsMockStore = create<FacebookSettingsMockState>(
  (set) => ({
    scenario:
      (sessionStorage.getItem(STORAGE_KEY) as FacebookSettingsMockScenario) ||
      'api',
    setScenario: (scenario) => {
      sessionStorage.setItem(STORAGE_KEY, scenario);
      set({ scenario });
    },
  }),
);

/** Canned settings payloads for each non-API mock scenario. */
export const FACEBOOK_MOCK_SCENARIOS: Record<
  Exclude<FacebookSettingsMockScenario, 'api'>,
  Omit<FacebookSettings, 'agency_id'>
> = {
  disconnected: {
    fb_connected: false,
    fb_lead_ad_budget: 1500,
    fb_lead_ad_duration: 7,
    ig_lead_ad_budget: 1500,
    ig_lead_ad_duration: 7,
    ...LEAD_AD_CONTENT_DEFAULTS,
    fb_marketplace_enabled: true,
    enabled_portals: [],
    ig_native_connected: false,
  },
  connected: {
    fb_connected: true,
    fb_page_id: '123456789',
    fb_page_name: 'Housien Social Teste',
    fb_ad_account_id: 'act_987654321',
    fb_lead_ad_budget: 2000,
    fb_lead_ad_duration: 14,
    ig_lead_ad_budget: 1800,
    ig_lead_ad_duration: 10,
    ...LEAD_AD_CONTENT_DEFAULTS,
    fb_marketplace_enabled: true,
    fb_has_payment_method: true,
    ig_account_id: '@meli.ctd',
    ig_account_name: '@meli.ctd',
    enabled_portals: ['facebook', 'instagram'],
    ig_native_connected: false,
  },
  'connected-no-payment': {
    fb_connected: true,
    fb_page_id: '123456789',
    fb_page_name: 'Housien Social Teste',
    fb_lead_ad_budget: 1500,
    fb_lead_ad_duration: 7,
    ig_lead_ad_budget: 1500,
    ig_lead_ad_duration: 7,
    ...LEAD_AD_CONTENT_DEFAULTS,
    fb_marketplace_enabled: true,
    fb_has_payment_method: false,
    ig_account_id: '@meli.ctd',
    ig_account_name: '@meli.ctd',
    enabled_portals: ['facebook'],
    ig_native_connected: false,
  },
};
